/**
 * Upload History — GC64 Phase A P0 win.
 * The tappable answer to "Where did my uploads go?"
 *
 * Every upload the platform has ever seen (new + retroactively
 * backfilled) is listed here with filename, date, purpose, linked
 * entity, size, status, and full audit trail. Actions per row:
 * Open · Download · Rename · Move · Soft-Delete · Restore.
 *
 * URL params:
 *   ?batch=<batchId>   → filter to that upload batch
 *   ?deleted=1         → show only soft-deleted rows
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Download, Edit3, Trash2, RotateCcw, MoveRight, ExternalLink,
  Filter, RefreshCw, ChevronDown, AlertTriangle,
} from "lucide-react";
import "./UploadHistory.css";

const API = process.env.REACT_APP_BACKEND_URL;
const OPTS = { withCredentials: true };

// GC64 · MP-10 (Feb 16, 2026) — timestamp display fix.
// See /app/frontend/src/utils/timezone.js for the full rationale
// and /app/frontend/src/pages/__tests__/UploadHistory.timezone.test.jsx
// for the DST / BST-GMT regression pins.
import { fmtDate } from "../utils/timezone";
export { fmtDate };

function fmtSize(bytes) {
  if (!bytes || Number.isNaN(bytes)) return "";
  const b = Number(bytes);
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

export default function UploadHistory() {
  const [params, setParams] = useSearchParams();
  const batchId = params.get("batch");
  const onlyDeleted = params.get("deleted") === "1";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [selected, setSelected] = useState(null);   // row for detail modal
  const [renaming, setRenaming] = useState(null);   // row being renamed
  const [renameValue, setRenameValue] = useState("");
  const [moving, setMoving] = useState(null);       // row being moved
  const [movePurpose, setMovePurpose] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const q = new URLSearchParams();
      if (batchId) q.set("batch_id", batchId);
      if (onlyDeleted) q.set("only_deleted", "true");
      q.set("limit", "200");
      const r = await axios.get(`${API}/api/history/uploads?${q.toString()}`, OPTS);
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Couldn't load.");
    } finally {
      setLoading(false);
    }
  }, [batchId, onlyDeleted]);

  useEffect(() => { load(); }, [load]);

  const openRow      = (row) => setSelected(row);
  const closeDetail  = () => setSelected(null);
  const openRename   = (row) => { setRenaming(row); setRenameValue(row?.meta?.filename || ""); };
  const openMove     = (row) => { setMoving(row); setMovePurpose(row?.meta?.purpose || ""); };
  const closeModals  = () => { setRenaming(null); setMoving(null); };

  const doRename = useCallback(async () => {
    if (!renaming || !renameValue.trim()) return;
    try {
      await axios.post(`${API}/api/history/uploads/${renaming.entity_id}/rename`,
        { new_filename: renameValue.trim() }, OPTS);
      closeModals(); load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Rename failed.");
    }
  }, [renaming, renameValue, load]);

  const doMove = useCallback(async () => {
    if (!moving) return;
    try {
      await axios.post(`${API}/api/history/uploads/${moving.entity_id}/move`,
        { target_purpose: movePurpose.trim() || null }, OPTS);
      closeModals(); load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Move failed.");
    }
  }, [moving, movePurpose, load]);

  const doSoftDelete = useCallback(async (row) => {
    if (!window.confirm(`Soft-delete "${row?.meta?.filename || 'this upload'}"? It will be hidden from active lists but preserved in history and can be restored.`)) return;
    try {
      await axios.post(`${API}/api/history/uploads/${row.entity_id}/soft-delete`, {}, OPTS);
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Delete failed.");
    }
  }, [load]);

  const doRestore = useCallback(async (row) => {
    try {
      await axios.post(`${API}/api/history/uploads/${row.entity_id}/restore`, {}, OPTS);
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Restore failed.");
    }
  }, [load]);

  const openFile = (row) => {
    // Best-effort deep link into the evidence detail view. If it 404s,
    // the detail modal remains the fallback.
    window.open(`/evidence/${row.entity_id}`, "_blank", "noopener,noreferrer");
  };

  const downloadFile = (row) => {
    // Same principle — attempt a signed-URL fetch. If your evidence API
    // provides one, the browser will negotiate. Otherwise the user sees
    // a friendly error via the caught rejection.
    window.open(`${API}/api/evidence/${row.entity_id}/download`, "_blank", "noopener,noreferrer");
  };

  const headline = useMemo(() => {
    if (batchId)     return `Uploads · this batch`;
    if (onlyDeleted) return `Deleted uploads`;
    return `My uploads`;
  }, [batchId, onlyDeleted]);

  return (
    <main className="upload-history" data-testid="upload-history-page">
      <header className="uh-head">
        <div>
          <h1>{headline}</h1>
          <p className="uh-sub">
            {total} {total === 1 ? "record" : "records"}
            {batchId && (<> · <Link to="/my/uploads" data-testid="uh-see-all">See all uploads</Link></>)}
          </p>
        </div>
        <div className="uh-head__actions">
          <button
            type="button"
            className="uh-btn"
            onClick={() => {
              const p = new URLSearchParams(params);
              if (onlyDeleted) { p.delete("deleted"); } else { p.set("deleted", "1"); }
              setParams(p);
            }}
            data-testid="uh-toggle-deleted"
          >
            <Filter aria-hidden="true" /> {onlyDeleted ? "Show active" : "Show deleted"}
          </button>
          <button
            type="button"
            className="uh-btn"
            onClick={load}
            disabled={loading}
            data-testid="uh-refresh"
            aria-label="Refresh"
          >
            <RefreshCw aria-hidden="true" className={loading ? "uh-spin" : ""} />
          </button>
        </div>
      </header>

      {err && (
        <div className="uh-alert" role="alert" data-testid="uh-error">
          <AlertTriangle aria-hidden="true" /> {err}
        </div>
      )}

      {items.length === 0 ? (
        <div className="uh-empty" data-testid="uh-empty">
          {onlyDeleted
            ? "No soft-deleted uploads."
            : batchId
              ? "No uploads in this batch (yet)."
              : "Nothing here yet. Uploads will appear as soon as you import a file."}
        </div>
      ) : (
        <ul className="uh-list" data-testid="uh-list">
          {items.map((row) => (
            <li
              key={row.id}
              className={`uh-row ${row.deleted_at ? "is-deleted" : ""}`}
              data-testid={`uh-row-${row.entity_id}`}
            >
              <div className="uh-row__body">
                <strong className="uh-row__title">{row?.meta?.filename || "Upload"}</strong>
                <span className="uh-row__meta">
                  {fmtDate(row.created_at)}
                  {row?.meta?.purpose && <> · {row.meta.purpose}</>}
                  {row?.meta?.size_bytes && <> · {fmtSize(row.meta.size_bytes)}</>}
                  {row?.status && <> · {row.status}</>}
                  {row.is_backfill && <> · <em>backfilled</em></>}
                </span>
                {row.deleted_at && (
                  <span className="uh-row__deleted" data-testid={`uh-row-deleted-${row.entity_id}`}>
                    Deleted {fmtDate(row.deleted_at)}
                  </span>
                )}
              </div>
              <div className="uh-row__actions">
                <button type="button" onClick={() => openRow(row)} data-testid={`uh-history-${row.entity_id}`} aria-label="History"><ChevronDown aria-hidden="true" /></button>
                {!row.deleted_at && <>
                  <button type="button" onClick={() => openFile(row)} data-testid={`uh-open-${row.entity_id}`} aria-label="Open"><ExternalLink aria-hidden="true" /></button>
                  <button type="button" onClick={() => downloadFile(row)} data-testid={`uh-download-${row.entity_id}`} aria-label="Download"><Download aria-hidden="true" /></button>
                  <button type="button" onClick={() => openRename(row)} data-testid={`uh-rename-${row.entity_id}`} aria-label="Rename"><Edit3 aria-hidden="true" /></button>
                  <button type="button" onClick={() => openMove(row)} data-testid={`uh-move-${row.entity_id}`} aria-label="Move"><MoveRight aria-hidden="true" /></button>
                  <button type="button" className="uh-btn--err" onClick={() => doSoftDelete(row)} data-testid={`uh-delete-${row.entity_id}`} aria-label="Delete"><Trash2 aria-hidden="true" /></button>
                </>}
                {row.deleted_at && (
                  <button type="button" className="uh-btn--ok" onClick={() => doRestore(row)} data-testid={`uh-restore-${row.entity_id}`} aria-label="Restore">
                    <RotateCcw aria-hidden="true" /> Restore
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Detail modal ------------------------------------------- */}
      {selected && (
        <div className="uh-modal-wrap" onClick={closeDetail} data-testid="uh-detail-modal">
          <div className="uh-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Audit history</h2>
            <p className="uh-modal__sub">{selected?.meta?.filename}</p>
            <UploadAuditTrail entityId={selected.entity_id} />
            <button type="button" onClick={closeDetail} className="uh-btn" data-testid="uh-detail-close">Close</button>
          </div>
        </div>
      )}

      {/* Rename modal ------------------------------------------- */}
      {renaming && (
        <div className="uh-modal-wrap" onClick={closeModals} data-testid="uh-rename-modal">
          <div className="uh-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Rename upload</h2>
            <p className="uh-modal__sub">Original filename is preserved in history.</p>
            <input
              type="text"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              autoFocus
              data-testid="uh-rename-input"
            />
            <div className="uh-modal__actions">
              <button type="button" onClick={closeModals} className="uh-btn" data-testid="uh-rename-cancel">Cancel</button>
              <button type="button" onClick={doRename} className="uh-btn uh-btn--ok" data-testid="uh-rename-save">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Move modal --------------------------------------------- */}
      {moving && (
        <div className="uh-modal-wrap" onClick={closeModals} data-testid="uh-move-modal">
          <div className="uh-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Move to a different purpose</h2>
            <p className="uh-modal__sub">
              Currently: <strong>{moving?.meta?.purpose || "unspecified"}</strong>
            </p>
            <label>
              New purpose
              <select
                value={movePurpose}
                onChange={(e) => setMovePurpose(e.target.value)}
                data-testid="uh-move-purpose"
              >
                <option value="">(unspecified)</option>
                <option value="sd_card_teaching_day_intake">Today&apos;s multi-camera recordings</option>
                <option value="single_camera_recording">Single-camera recording</option>
                <option value="general_video">Video</option>
                <option value="photograph">Photograph</option>
                <option value="voice_note">Voice note</option>
                <option value="business_receipt">Petrol / business receipt</option>
                <option value="vehicle_document">Vehicle document</option>
                <option value="lesson_evidence">Lesson evidence</option>
                <option value="driving_test_evidence">Driving test evidence</option>
                <option value="business_document">Business document</option>
                <option value="educational_resource">Educational resource</option>
                <option value="intelligence_contribution">Intelligence contribution</option>
                <option value="other">Something else</option>
              </select>
            </label>
            <div className="uh-modal__actions">
              <button type="button" onClick={closeModals} className="uh-btn" data-testid="uh-move-cancel">Cancel</button>
              <button type="button" onClick={doMove} className="uh-btn uh-btn--ok" data-testid="uh-move-save">Move</button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

/* Audit trail component — fetches full history for the entity */
function UploadAuditTrail({ entityId }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const q = new URLSearchParams({ entity_type: "upload", include_deleted: "true", limit: "50" });
        const r = await axios.get(`${API}/api/history/records?${q.toString()}`, OPTS);
        const all = r.data?.items || [];
        setRows(all.filter((x) => x.entity_id === entityId));
      } catch { setRows([]); }
    })();
  }, [entityId]);
  if (rows === null) return <p>Loading…</p>;
  if (rows.length === 0) return <p>No events.</p>;
  return (
    <ol className="uh-trail" data-testid="uh-audit-trail">
      {rows.map((r) => (
        <li key={r.id}>
          <strong>{r.action}</strong>
          <span>{fmtDate(r.created_at)}</span>
          {r.prev_values && r.new_values && (
            <div className="uh-trail__delta">
              was <code>{JSON.stringify(r.prev_values)}</code> → <code>{JSON.stringify(r.new_values)}</code>
            </div>
          )}
        </li>
      ))}
    </ol>
  );
}
